// @ts-nocheck
import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { goBackToOrigin } from "../utils/nav";
import { CookingInterface } from "../components/recipe/CookingInterface";
// AppFrame removed: route-level layout wraps pages
import { useQuery } from "wasp/client/operations";
import {
  getRecipeForCooking,
  startCookingSession,
  completeCookingSession,
  patchCookingProgress,
} from "wasp/client/operations";
import { Button } from "../components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { ArrowLeft, ChefHat } from "lucide-react";
import { useAuth } from "wasp/client/auth";
import { trackEvent } from "../config/analytics";

interface RecipeStep {
  id: string;
  instruction: string;
  duration?: number;
  temperature?: string;
  ingredients?: string[];
}

interface RecipeIngredientView {
  name: string;
  quantity?: number;
  unit?: string;
  notes?: string;
  ingredientId?: string;
}

interface Recipe {
  id: string;
  title: string;
  servings: number;
  prepTime: number;
  cookTime: number;
  steps: RecipeStep[];
  ingredients: RecipeIngredientView[];
}

export function CookingModePage() {
  const { recipeId } = useParams<{ recipeId: string }>();
  const navigate = useNavigate();
  const { data: user } = useAuth();
  const location = useLocation();
  const fromPath = (location.state as any)?.from as string | undefined;

  const [cookingSessionId, setCookingSessionId] = useState<string | null>(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [completedSteps, setCompletedSteps] = useState<Set<number>>(new Set());
  const [sessionVersion, setSessionVersion] = useState(1);

  const { data, isLoading, error } = useQuery(getRecipeForCooking, {
    recipeId: recipeId!,
  });

  const recipe = data?.recipe
    ? {
        id: data.recipe.id,
        title: data.recipe.title,
        servings: data.recipe.servings,
        prepTime: data.recipe.totalMinutes || 0,
        cookTime: data.recipe.totalMinutes || 0,
        steps: data.recipe.steps.map((step: any, index: number) => ({
          id: step.id,
          instruction: step.text,
          duration: step.durationSeconds
            ? step.durationSeconds / 60
            : undefined,
          ingredients: step.ingredientRefs || [],
        })),
        ingredients: data.recipe.ingredients.map((ing: any) => ({
          ingredientId: ing.ingredientId,
          name: ing.ingredient?.name || ing.name || '',
          quantity: typeof ing.quantity === 'number' ? ing.quantity : undefined,
          unit: ing.unit || ing.displayUnit || undefined,
          notes: ing.notes || undefined,
        })),
      }
    : undefined;

  // Start cooking session when component mounts
  useEffect(() => {
    if (recipeId && user && !cookingSessionId) {
      startCookingSession({ recipeId, deviceId: "web" })
        .then((result) => {
          setCookingSessionId(result.sessionId);
        })
        .catch((error) => {
          console.error("Failed to start cooking session:", error);
        });
    }
  }, [recipeId, user, cookingSessionId]);

  const handleStepComplete = async (stepIndex: number) => {
    const newCompleted = new Set(completedSteps);
    newCompleted.add(stepIndex);
    setCompletedSteps(newCompleted);

    // Update backend session
    if (cookingSessionId) {
      try {
        await patchCookingProgress({
          sessionId: cookingSessionId,
          version: sessionVersion,
          patch: {
            currentStep: stepIndex + 1,
            completedStepIds: Array.from(newCompleted).map((i) => i.toString()),
          },
        });
        setSessionVersion((prev) => prev + 1);
      } catch (error) {
        console.error("Failed to update cooking progress:", error);
      }
    }
  };

  const handleSessionComplete = async () => {
    if (cookingSessionId) {
      try {
        await completeCookingSession({ sessionId: cookingSessionId });
        try {
          trackEvent('cooking_mode_completed', { recipeId });
        } catch {}
        goBackToOrigin(navigate, location, '/discover');
      } catch (error) {
        console.error("Failed to complete cooking session:", error);
      }
    }
  };

  const handleClose = () => {
    goBackToOrigin(navigate, location, '/discover');
  };

  if (isLoading) {
    return (
        <div className="flex min-h-[400px] items-center justify-center">
          <div className="text-center">
            <ChefHat className="mx-auto mb-4 h-12 w-12 animate-pulse text-coral" />
            <p className="text-gray-600">Loading recipe for cooking...</p>
          </div>
        </div>
    );
  }

  if (error || !recipe) {
    return (
        <div className="flex min-h-[400px] items-center justify-center">
          <Card className="w-full max-w-md">
            <CardHeader>
              <CardTitle className="text-red-600">Recipe Not Found</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="mb-4 text-gray-600">
                We couldn't find the recipe you're looking for. It may have been
                deleted or you may not have permission to view it.
              </p>
              <Button onClick={() => navigate("/discover")} className="w-full">
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to Discover
              </Button>
            </CardContent>
          </Card>
        </div>
    );
  }

  return (
      <CookingInterface
        recipe={recipe}
        onClose={handleClose}
        onComplete={handleSessionComplete}
      />
  );
}
// @ts-nocheck
